import { Quote, selectClient, selectMaybeClientPartner } from '@/model';
import { isShortTermCareQuote, isTraditionalLTCQuote } from '@/util/quoteTypeGuards';
import { useSelector } from 'react-redux';

interface UseQuoteSelectionProps {
  selectedTab: ShopInsuranceTab;
}

export function useQuoteSelection({ selectedTab }: UseQuoteSelectionProps) {
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);

  const isQuoteSelected = (
    quote: Quote | TraditionalLTCQuote | ShortTermCareQuote,
  ): { isSelected: boolean; fundingSourceId: string | null } => {
    if (!client || !client.multipleFundingSources?.ltcPolicy) {
      return { isSelected: false, fundingSourceId: null };
    }

    const checkClientPolicies = (clientToCheck: Client) => {
      if (!clientToCheck.multipleFundingSources?.ltcPolicy) {
        return { isSelected: false, fundingSourceId: null };
      }

      for (const [fundingSourceId, policy] of Object.entries(
        clientToCheck.multipleFundingSources.ltcPolicy,
      )) {
        let quoteIdMatches = false;
        let premiumMatches = false;

        if (isShortTermCareQuote(quote)) {
          // Must precede the life-insurance branch below: an STC quote has no
          // premiumAmount, so falling through would compare against undefined
          // and report every STC quote as unselected.
          quoteIdMatches = policy.insuranceQuoteId === quote.shortTermCareQuoteId;
          premiumMatches = true;
        } else if (isTraditionalLTCQuote(quote)) {
          quoteIdMatches = policy.insuranceQuoteId === quote.traditionalLTCQuoteId;
          premiumMatches = true;
        } else {
          const PREMIUM_TOLERANCE = 1;
          if (policy.policyLimitedPayYears && policy.policyPremiumMonthlyCost) {
            const annualPremium = policy.policyPremiumMonthlyCost * 12;
            premiumMatches = Math.abs(annualPremium - quote.premiumAmount) < PREMIUM_TOLERANCE;
          } else if (policy.policyLumpSumPayment && policy.policyLumpSumPayment > 0) {
            premiumMatches =
              Math.abs(policy.policyLumpSumPayment - quote.premiumAmount) < PREMIUM_TOLERANCE;
          }
          quoteIdMatches = policy.insuranceQuoteId === quote.insuranceQuoteId;
        }

        if (quoteIdMatches && premiumMatches) {
          return { isSelected: true, fundingSourceId };
        }
      }

      return { isSelected: false, fundingSourceId: null };
    };

    if (selectedTab === 'joint' && 'policyType' in quote && quote.policyType === 'Joint Life') {
      const currentClientResult = checkClientPolicies(client);
      const partnerResult = clientPartner
        ? checkClientPolicies(clientPartner)
        : { isSelected: false, fundingSourceId: null };

      if (currentClientResult.isSelected && partnerResult.isSelected) {
        return currentClientResult;
      }

      return { isSelected: false, fundingSourceId: null };
    } else {
      return checkClientPolicies(client);
    }
  };

  return {
    isQuoteSelected,
  };
}
